import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useSeriesSuggestions() {
  const queryClient = useQueryClient();

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ['series-suggestions'],
    queryFn: async () => {
      const { data: suggestionsData, error: suggestionsError } = await supabase
        .from('series_suggestions')
        .select(`
          *,
          series_books (
            id,
            position_in_series,
            is_found,
            temp_title,
            temp_isbn13,
            book_id,
            book:books (
              id,
              title,
              cover_url,
              isbn13,
              isbn10
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (suggestionsError) throw suggestionsError;

      // Transform the data to match our UI structure
      return suggestionsData?.map(suggestion => ({
        id: suggestion.id,
        seriesName: suggestion.series_name,
        authorName: suggestion.author_name,
        confidence: Math.round((suggestion.confidence_score || 0.5) * 10), // Convert 0-10 scale to percentage
        source: suggestion.source,
        booksFound: suggestion.series_books?.filter(b => b.is_found).length || 0,
        expectedBooks: suggestion.total_books_expected || 0,
        status: suggestion.status,
        // Count all books (both linked and temp)
        totalBooks: suggestion.series_books?.length || 0,
        books: suggestion.series_books
          ?.filter(b => b.is_found && b.book)
          .map(sb => ({
            id: sb.book.id,
            title: sb.book.title,
            position: sb.position_in_series,
            isbn: sb.book.isbn13 || sb.book.isbn10 || 'Unknown',
            coverUrl: sb.book.cover_url || '/placeholder.svg',
          }))
          .sort((a, b) => a.position - b.position) || [],
        gaps: Array.from(
          { length: suggestion.total_books_expected || 0 },
          (_, i) => i + 1
        ).filter(
          pos => !suggestion.series_books?.some(sb => sb.position_in_series === pos && sb.is_found)
        ),
        // Include temp book data for display purposes
        tempBooks: suggestion.series_books
          ?.filter(b => !b.is_found && (b.temp_title || b.temp_isbn13))
          .map(sb => ({
            position: sb.position_in_series,
            title: sb.temp_title,
            isbn: sb.temp_isbn13,
          })) || [],
      })) || [];
    },
  });

  // CRITICAL: Approve now persists series data to the books table
  const approveSuggestion = useMutation({
    mutationFn: async (suggestionId: string) => {
      // 1. Get the full series suggestion with all books
      const { data: suggestion, error: fetchError } = await supabase
        .from('series_suggestions')
        .select(`
          id,
          series_name,
          author_name,
          total_books_expected,
          series_books (
            id,
            book_id,
            position_in_series,
            is_found
          )
        `)
        .eq('id', suggestionId)
        .single();

      if (fetchError) throw fetchError;
      if (!suggestion) throw new Error('Series not found');

      // 2. Update all linked books with series metadata
      const linkedBooks = suggestion.series_books?.filter(sb => sb.is_found && sb.book_id) || [];
      const totalBooks = suggestion.total_books_expected || linkedBooks.length;
      
      let updatedCount = 0;
      for (const seriesBook of linkedBooks) {
        const { error: updateError } = await supabase
          .from('books')
          .update({
            series_name: suggestion.series_name,
            series_position: seriesBook.position_in_series,
            series_total: totalBooks,
          })
          .eq('id', seriesBook.book_id);

        if (updateError) {
          console.error(`Failed to update book ${seriesBook.book_id}:`, updateError);
        } else {
          updatedCount++;
        }
      }

      // 3. Mark the suggestion as approved
      const { error: approveError } = await supabase
        .from('series_suggestions')
        .update({ 
          status: 'approved', 
          reviewed_at: new Date().toISOString() 
        })
        .eq('id', suggestionId);

      if (approveError) throw approveError;

      return { updatedCount, totalLinked: linkedBooks.length };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['imported-series'] });
      toast.success(`Series approved! ${result.updatedCount} books now have series metadata.`);
    },
    onError: (error: any) => {
      toast.error(`Failed to approve: ${error.message}`);
    },
  });

  const rejectSuggestion = useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase
        .from('series_suggestions')
        .update({ status: 'rejected', reviewed_at: new Date().toISOString() })
        .eq('id', suggestionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
      toast.success('Series rejected');
    },
    onError: (error: any) => {
      toast.error(`Failed to reject: ${error.message}`);
    },
  });

  const updateSuggestion = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase
        .from('series_suggestions')
        .update({
          series_name: updates.seriesName,
          series_name_normalized: updates.seriesName.toLowerCase().trim(),
          author_name: updates.authorName,
          author_name_normalized: updates.authorName.toLowerCase().trim(),
          total_books_expected: updates.expectedBooks,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series-suggestions'] });
      toast.success('Series updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  return {
    suggestions: suggestions?.filter(s => s.status === 'pending') || [],
    allSuggestions: suggestions || [],
    isLoading,
    approveSuggestion: approveSuggestion.mutate,
    rejectSuggestion: rejectSuggestion.mutate,
    updateSuggestion: updateSuggestion.mutate,
    isApproving: approveSuggestion.isPending,
  };
}
